import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Footer from '../components/Footer';
import PodcastPlayer from '../components/PodcastPlayer';
import perInfographic from '../assets/per-infographic-paysage.jpg';

// Import des logos
import oradeaLogo from '../assets/logos/oradea.png';
import generaliLogo from '../assets/logos/generali.png';
import abeilleLogo from '../assets/logos/abeille.png';
import aprilLogo from '../assets/logos/april.png';

const {
  FiArrowRight, FiCheck, FiTrendingUp, FiShield, FiAward, FiUserCheck,
  FiBriefcase, FiMonitor, FiPhoneCall, FiSend, FiHome, FiUnlock,
  FiPercent, FiLeaf, FiLock, FiHeart, FiAnchor, FiStar, FiChevronUp, FiTarget, FiClock,
  FiDollarSign, FiPiggyBank, FiCalendar, FiGift, FiUsers
} = FiIcons;

const partners = [
    { logo: oradeaLogo, alt: "Oradea Vie Logo", text: "Oradéa Vie — Filiale du groupe Société Générale, l’un des plus grands groupes bancaires européens, reconnue pour son expertise pointue en épargne patrimoniale et sa distribution via des réseaux spécialisés de conseillers." },
    { logo: generaliLogo, alt: "Generali Logo", text: "Generali — Groupe d’assurance parmi les leaders mondiaux, présent dans plus de 50 pays et gérant plusieurs dizaines de milliards d’euros en épargne retraite en France." },
    { logo: abeilleLogo, alt: "Abeille Assurances Logo", text: "Abeille Assurances — Acteur majeur de l’assurance en France, intégré au groupe mutualiste Aéma (plusieurs millions d’assurés), reconnu pour son savoir-faire historique en épargne et retraite." },
    { logo: aprilLogo, alt: "April Logo", text: "APRIL — Leader du courtage grossiste en France, présent à l’international et s’appuyant sur un réseau de plus de 30 000 courtiers partenaires." }
];

const HomeLight = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [currentExampleIndex, setCurrentExampleIndex] = useState(0);
  const [activePartnerIndex, setActivePartnerIndex] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const simulationExamples = [
    {
      type: 'graph',
      title: 'Projection sur 30 ans',
      subtitle: 'Versement de 367€/mois (TMI 11%)',
      finalCapital: '348 197 €',
      taxSavings: '15 502 €',
      chartData: [
        { year: 5, invested: 22020, interest: 3500 },
        { year: 10, invested: 44040, interest: 14000 },
        { year: 15, invested: 66060, interest: 38000 },
        { year: 20, invested: 88080, interest: 72000 },
        { year: 25, invested: 110100, interest: 125000 },
        { year: 30, invested: 132120, interest: 216077 },
      ]
    },
    {
      type: 'profile',
      name: 'Clara B.',
      details: '41 ans, Responsable Logistique',
      income: '3 007 €/mois',
      savings: '300 €/mois',
      tmi: '11%',
      duration: '25 ans',
      finalCapital: '180 408 €',
      annualTaxSaving: '396 €',
      totalTaxSaving: '9 900 €'
    },
    {
      type: 'profile',
      name: 'Manon P.',
      details: '47 ans, Vendeur(se) en boutique',
      income: '2 282 €/mois',
      savings: '250 €/mois',
      tmi: '11%',
      duration: '17 ans',
      finalCapital: '81 397 €',
      annualTaxSaving: '330 €',
      totalTaxSaving: '5 610 €'
    },
    {
      type: 'profile',
      name: 'Maxime L.',
      details: '37 ans, Restaurateur(e)',
      income: '6 363 €/mois',
      savings: '650 €/mois',
      tmi: '30%',
      duration: '26 ans',
      finalCapital: '418 619 €',
      annualTaxSaving: '2 340 €',
      totalTaxSaving: '60 840 €',
      children: '2 enfants'
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);

    const exampleInterval = setInterval(() => {
      setCurrentExampleIndex(prevIndex => (prevIndex + 1) % simulationExamples.length);
    }, 8000);

    const partnerInterval = setInterval(() => {
        setActivePartnerIndex(prevIndex => (prevIndex + 1) % partners.length);
    }, 5000);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearInterval(exampleInterval);
      clearInterval(partnerInterval);
    };
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const redirectToForm = () => {
    navigate(`/simulation${location.search}`);
  };

  const currentExample = simulationExamples[currentExampleIndex];

  return (
    <div className="min-h-screen bg-white font-sans overflow-x-hidden text-[#004d44]">
      {/* Bouton Retour en Haut */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-8 right-8 z-[100] w-12 h-12 bg-[#00a48c] text-white rounded-full shadow-2xl flex items-center justify-center hover:bg-[#00856f] transition-all"
          >
            <SafeIcon icon={FiChevronUp} size={24} />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg py-3' : 'bg-transparent py-4 md:py-6'}`}>
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}>
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-black ${isScrolled ? 'bg-[#00a48c] text-white' : 'bg-white text-[#00a48c]'}`}>S</div>
            <span className={`text-lg md:text-xl font-black tracking-tight ${isScrolled ? 'text-[#004d44]' : 'text-white'}`}>Simulation PER</span>
          </div>
          <div className="hidden lg:flex items-center space-x-8 font-bold text-sm uppercase tracking-wide">
            <button onClick={() => scrollTo('avantages')} className={`${isScrolled ? 'text-[#006b5a]' : 'text-white/90'} hover:text-[#00a48c] transition-colors`}>Avantages</button>
            <button onClick={() => scrollTo('comment-ca-marche')} className={`${isScrolled ? 'text-[#006b5a]' : 'text-white/90'} hover:text-[#00a48c] transition-colors`}>Comment ça marche</button>
            <button onClick={() => scrollTo('fiscalite')} className={`${isScrolled ? 'text-[#006b5a]' : 'text-white/90'} hover:text-[#00a48c] transition-colors`}>Réduction d’impôt</button>
            <button onClick={redirectToForm} className="bg-[#00a48c] text-white px-6 py-2.5 rounded-full hover:bg-[#00856f] transition-all shadow-md">Simulation</button>
          </div>
          <button onClick={redirectToForm} className="lg:hidden bg-white text-[#00a48c] px-4 py-2 rounded-full font-black text-xs uppercase shadow-lg">Simuler</button>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="comparateur" className="relative pt-24 md:pt-48 pb-12 md:pb-32 bg-gradient-to-br from-[#00a48c] to-[#006b5a] overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'radial-gradient(#fff 2px,transparent 2px)', backgroundSize: '30px 30px' }}></div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}>
              <h1 className="text-3xl md:text-7xl font-black text-white uppercase tracking-tight leading-[1.1] mb-4 md:mb-6 text-center lg:text-left">
                PLAN ÉPARGNE <br className="hidden md:block"/> RETRAITE
              </h1>
              <h2 className="text-lg md:text-2xl font-bold text-[#aaffd6] leading-snug mb-4 text-center lg:text-left">
                Simulez vos gains en 2 cliques et choisissez les meilleurs PER en toute simplicité.
              </h2>
              <p className="text-white/80 text-xs md:text-base font-medium mb-8 leading-relaxed text-center lg:text-left max-w-2xl">
                Bénéficiez de l'expertise de nos conseillers pour choisir le meilleur Plan d'Épargne Retraite qui vous convient. Optez pour une retraite plus confortable demain et une baisse d'impôts aujourd'hui.
              </p>
              <div className="flex justify-center lg:justify-start">
                <button onClick={redirectToForm} className="bg-white text-[#00856f] px-8 md:px-10 py-3 md:py-4 rounded-full font-black text-base md:text-lg shadow-2xl hover:scale-105 transition-all mb-8 md:mb-12 flex items-center gap-3">
                  Démarrer la simulation <SafeIcon icon={FiArrowRight} />
                </button>
              </div>
              <div className="flex items-center justify-center lg:justify-start gap-6 md:gap-8 opacity-90">
                <div className="flex flex-col items-center">
                  <span className="font-serif font-black text-white text-lg md:text-xl uppercase">ACPR</span>
                  <span className="text-[6px] md:text-[7px] font-bold text-white uppercase tracking-widest text-center">Banque de France</span>
                </div>
                <div className="h-6 w-px bg-white/30"></div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-white text-lg md:text-xl tracking-tight uppercase">Orias</span>
                  <div className="flex gap-0.5 items-end h-4 md:h-6">
                    <div className="w-1 h-2 bg-white/40"></div>
                    <div className="w-1 h-4 bg-white/60"></div>
                    <div className="w-1 h-1.5 bg-white/30"></div>
                  </div>
                </div>
              </div>
            </motion.div>

            <div className="mt-8 lg:mt-0">
              <div className="bg-white p-5 md:p-8 rounded-[2rem] md:rounded-[3rem] shadow-2xl relative cursor-pointer overflow-hidden" onClick={redirectToForm}>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentExampleIndex}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5 }}
                  >
                    {currentExample.type === 'graph' && (
                      <div>
                        <div className="grid grid-cols-2 gap-3 md:gap-4 mb-4 md:mb-6">
                          <div className="bg-[#f0fcf9] rounded-xl md:rounded-2xl p-3 md:p-4 border border-[#e0f2ee]">
                            <div className="text-[8px] md:text-[9px] text-[#00856f] font-bold uppercase mb-1">Projection 30 ans</div>
                            <div className="text-lg md:text-xl font-black text-[#004d44]">{currentExample.finalCapital}</div>
                          </div>
                          <div className="bg-gray-50 rounded-xl md:rounded-2xl p-3 md:p-4 border border-gray-100">
                            <div className="text-[8px] md:text-[9px] text-[#006b5a]/50 font-bold uppercase mb-1">Gain Fiscal Total</div>
                            <div className="text-lg md:text-xl font-black text-[#00a48c]">{currentExample.taxSavings}</div>
                          </div>
                        </div>
                        <div className="h-[140px] md:h-[180px] w-full flex items-end justify-between px-1 md:px-4 space-x-1 md:space-x-4">
                          {currentExample.chartData.map((data, i) => (
                            <div key={i} className="flex-1 flex flex-col justify-end h-full">
                              <div className="relative flex flex-col-reverse w-full items-center">
                                <motion.div initial={{ height: 0 }} animate={{ height: `${data.invested * 0.0003}px` }} transition={{ duration: 0.8, ease: 'easeOut', delay: i * 0.1 }} className="w-full bg-[#006b5a] rounded-sm" />
                                <motion.div initial={{ height: 0 }} animate={{ height: `${data.interest * 0.0003}px` }} transition={{ duration: 0.8, ease: 'easeOut', delay: i * 0.1 }} className="w-full bg-[#00e5c1] rounded-t-md relative shadow-sm" />
                              </div>
                              <div className="mt-2 text-[7px] md:text-[8px] font-bold text-[#006b5a]/40 text-center">{data.year} ans</div>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-center items-center gap-4 md:gap-6 mt-4">
                          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-sm bg-[#006b5a]"></div><span className="text-[9px] md:text-[10px] font-bold text-[#006b5a]/70 uppercase tracking-wider">Capital</span></div>
                          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-sm bg-[#00e5c1]"></div><span className="text-[9px] md:text-[10px] font-bold text-[#006b5a]/70 uppercase tracking-wider">Intérêts</span></div>
                        </div>
                        <p className="mt-2 text-[8px] md:text-[9px] leading-tight md:leading-relaxed text-[#006b5a]/50 font-medium italic text-center">{currentExample.subtitle}</p>
                      </div>
                    )}
                    {currentExample.type === 'profile' && (
                      <div className="min-h-[320px] md:min-h-[380px] flex flex-col justify-between">
                        <div>
                          <h3 className="text-2xl md:text-3xl font-black text-[#004d44]">{currentExample.name}</h3>
                          <p className="text-sm md:text-base font-medium text-gray-500 mb-4">{currentExample.details}</p>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs md:text-sm mb-4 border-y border-gray-100 py-3">
                              <div className="flex items-center gap-2"><SafeIcon icon={FiDollarSign} className="text-[#00a48c]"/> <span className="font-bold">{currentExample.income}</span></div>
                              <div className="flex items-center gap-2"><SafeIcon icon={FiPiggyBank} className="text-[#00a48c]"/> <span className="font-bold">{currentExample.savings}</span></div>
                              <div className="flex items-center gap-2"><SafeIcon icon={FiPercent} className="text-[#00a48c]"/> <span className="font-bold">TMI {currentExample.tmi}</span></div>
                              <div className="flex items-center gap-2"><SafeIcon icon={FiCalendar} className="text-[#00a48c]"/> <span className="font-bold">sur {currentExample.duration}</span></div>
                              {currentExample.children && (
                                <div className="flex items-center gap-2"><SafeIcon icon={FiUsers} className="text-[#00a48c]"/> <span className="font-bold">{currentExample.children}</span></div>
                              )}
                          </div>
                        </div>
                        
                        <div className="my-auto py-4 space-y-6">
                          <div className="text-center">
                              <div className="w-12 h-12 mx-auto bg-[#f0fcf9] rounded-full flex items-center justify-center shadow-md mb-2 border border-gray-100">
                                  <SafeIcon icon={FiTarget} className="text-[#00a48c]" size={24} />
                              </div>
                              <p className="text-sm font-bold text-[#006b5a] uppercase tracking-wider">Capital Retraite Estimé</p>
                              <p className="text-3xl md:text-4xl font-black text-[#004d44] my-1">{currentExample.finalCapital}</p>
                          </div>
                          <div className="text-center">
                              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Économie d'impôt annuelle</p>
                              <p className="text-2xl font-black text-[#00a48c]">{currentExample.annualTaxSaving}</p>
                          </div>
                        </div>

                        <p className="text-[8px] md:text-[9px] text-gray-400 font-medium text-center mt-2">
                          * Estimation basée sur un rendement de 5%/an (profil équilibré). Le résultat dépend du profil de gestion.
                        </p>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
                <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-gray-200">
                  <motion.div
                    key={currentExampleIndex}
                    initial={{ width: '0%' }}
                    animate={{ width: '100%' }}
                    transition={{ duration: 8, ease: 'linear' }}
                    className="h-full bg-[#00a48c]/70"
                  />
                </div>
              </div>
              <motion.div 
                className="mt-8 text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0, transition: { delay: 0.5 } }}
              >
                <p className="text-white/90 font-bold text-lg mb-4">
                  À votre tour de découvrir votre potentiel.
                </p>
                <button 
                  onClick={redirectToForm} 
                  className="bg-white text-[#00856f] px-8 py-3 rounded-full font-black text-base shadow-lg hover:scale-105 transition-all flex items-center gap-2 mx-auto"
                >
                  Estimer mon capital & mes impôts <SafeIcon icon={FiArrowRight} />
                </button>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Podcast & Infographic Section */}
      <section className="py-12 md:py-24 bg-[#f0fcf9]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img 
                src={perInfographic} 
                alt="Infographie sur les avantages du Plan Épargne Retraite" 
                className="rounded-3xl shadow-2xl w-full h-full object-cover"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <PodcastPlayer />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Partenaires */}
      <section className="py-12 md:py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center mb-10 md:mb-16 max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-5xl font-black text-[#004d44] mb-4 tracking-tight uppercase">Notre réseau de partenaires</h2>
            <p className="text-sm md:text-lg text-gray-500 font-medium leading-relaxed">
              Nous vous aidons à gagner en sécurité financière et en tranquillité d'esprit en nous associant aux entreprises les plus prestigieuses du secteur.
            </p>
            <div className="w-16 h-1 bg-[#00a48c] mx-auto mt-6"></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {partners.map((partner, index) => (
                <div
                    key={index}
                    onClick={() => setActivePartnerIndex(index)}
                    className="flex justify-center items-center h-32 cursor-pointer"
                >
                    <motion.img
                        src={partner.logo}
                        alt={partner.alt}
                        animate={activePartnerIndex === index ? "active" : "inactive"}
                        variants={{
                            active: { filter: 'grayscale(0%)', scale: 1.1, opacity: 1 },
                            inactive: { filter: 'grayscale(100%)', scale: 1, opacity: 0.6 }
                        }}
                        whileHover={{ filter: 'grayscale(0%)', opacity: 1 }}
                        transition={{ duration: 0.4, ease: 'easeInOut' }}
                        className="max-h-16 w-auto"
                    />
                </div>
            ))}
          </div>
          <div className="mt-12 h-24 flex items-center justify-center">
              <AnimatePresence mode="wait">
                  <motion.p
                      key={activePartnerIndex}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3 }}
                      className="text-center text-sm md:text-base text-gray-600 font-medium max-w-3xl mx-auto"
                  >
                      {partners[activePartnerIndex].text}
                  </motion.p>
              </AnimatePresence>
          </div>
        </div>
      </section>

      {/* Réduction impôt */}
      <section id="fiscalite" className="py-12 md:py-24 bg-gradient-to-b from-white to-[#f0fcf9]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-6xl font-black text-[#006b5a] mb-6 md:mb-8 uppercase tracking-tight leading-tight md:leading-[0.9]">
                Réduisez vos <br className="hidden md:block"/> impôts
              </h2>
              <div className="space-y-6 md:space-y-8 pr-0 md:pr-12">
                <p className="text-sm md:text-lg text-[#006b5a]/80 font-medium leading-relaxed">
                  En versant des cotisations à votre Plan d'Epargne Retraite (PER), vous pouvez réduire le montant de votre revenu soumis à l'impôt. Plus votre revenu est élevé, plus la réduction d'impôts sera importante en fonction du barème d'imposition applicable à vos revenus.
                </p>
                <div className="bg-white p-6 rounded-2xl border border-[#00a48c]/20 shadow-sm relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-1 h-full bg-[#00a48c]" />
                  <p className="text-sm md:text-base text-[#004d44] font-bold italic leading-relaxed">
                    <span className="text-[#00a48c]">Exemple :</span> Sur un revenu imposable de <span className="underline decoration-[#00a48c]/30">90 000€</span> et un versement PER de <span className="underline decoration-[#00a48c]/30">9 000€</span> vous bénéficiez d'un avantage fiscal de <span className="text-[#00a48c] text-lg">3 690€</span>.
                  </p>
                </div>
                <p className="text-xs md:text-sm text-[#006b5a]/60 font-bold uppercase tracking-wider">
                  Dans certains cas, le taux de réduction d'impôts peut aller jusqu'à 45%.
                </p>
              </div>
              <div className="flex justify-center lg:justify-start">
                <button onClick={redirectToForm} className="mt-8 md:mt-12 bg-[#00a48c] text-white px-8 md:px-10 py-4 rounded-full font-black shadow-xl hover:bg-[#00856f] transition-all flex items-center gap-3">
                  Calculer mon économie <SafeIcon icon={FiArrowRight} />
                </button>
              </div>
            </div>
            <div className="relative cursor-pointer mt-8 lg:mt-0" onClick={redirectToForm}>
              <div className="bg-gradient-to-br from-[#00a48c] to-[#006b5a] rounded-[2rem] md:rounded-[3.5rem] p-8 md:p-10 text-white shadow-2xl relative overflow-hidden">
                <div className="relative z-10">
                  <span className="text-[#aaffd6] font-black uppercase text-[10px] md:text-xs tracking-[0.3em] block mb-2">Avantage fiscal net</span>
                  <span className="text-5xl md:text-7xl font-black text-white tracking-tighter">3 690 €</span>
                  <div className="mt-6 pt-6 border-t border-white/20">
                     <p className="text-[10px] md:text-xs font-bold text-white/70 uppercase">Exemple pour un revenu de 90k€/an</p>
                  </div>
                </div>
                <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise */}
      <section id="a-propos" className="py-12 md:py-24 bg-[#f0fcf9]">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-black text-[#004d44] mb-6 md:mb-8 max-w-5xl mx-auto leading-tight uppercase tracking-tight">
            L’expertise PER pour les travailleurs non-salariés, cadres, et hauts revenus
          </h2>
          <p className="text-sm md:text-lg text-[#006b5a]/80 font-medium leading-relaxed max-w-4xl mx-auto mb-10 md:mb-16">
            Nos experts comprennent et connaissent les caractéristiques, besoins et limites des professions des travailleurs non-salariés (TNS) pour proposer l’investissement qui vous convient. Nous sommes spécialisés sur l’accompagnement des travailleurs non-salariés (tns), chefs d’entreprise, entrepreneurs, cadres et dirigeants, proposant des produits d’épargne-retraite premiums sur-mesure.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-6">
            {[
              { label: 'TNS', icon: FiUserCheck },
              { label: 'Chefs d’entreprise', icon: FiBriefcase },
              { label: 'Entrepreneurs', icon: FiTrendingUp },
              { label: 'Cadres', icon: FiAward },
              { label: 'Dirigeants', icon: FiTarget }
            ].map((item, idx) => (
              <motion.div key={idx} whileHover={{ y: -5 }} onClick={redirectToForm} className="bg-white p-5 md:p-8 rounded-2xl shadow-xl border border-[#00a48c]/10 flex flex-col items-center justify-center text-center cursor-pointer transition-all" >
                <div className="w-10 h-10 md:w-14 md:h-14 rounded-xl bg-[#f0fcf9] text-[#00a48c] flex items-center justify-center mb-3 md:mb-5 text-xl md:text-2xl">
                  <SafeIcon icon={item.icon} />
                </div>
                <span className="text-[10px] md:text-sm font-black text-[#004d44] leading-tight uppercase tracking-tighter">
                  {item.label}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section Avantages */}
      <section id="avantages" className="py-12 md:py-24 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-5xl font-black text-[#004d44] mb-4 uppercase tracking-tight">Pourquoi ouvrir un PER ?</h2>
            <p className="text-[#006b5a]/60 font-bold text-sm md:text-base uppercase tracking-widest">Une solution souple et performante pour votre épargne</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[
              { icon: FiPercent, title: "Réduction d'impôts", desc: "Déduisez vos versements de vos revenus imposables et réalisez une économie d'impôt immédiate selon votre TMI." },
              { icon: FiUnlock, title: "Sortie en Capital", desc: "À la retraite, récupérez 100% de votre épargne en capital (en une ou plusieurs fois) ou optez pour une rente viagère." },
              { icon: FiHome, title: "Résidence Principale", desc: "Votre épargne n'est pas bloquée ! Débloquez votre capital par anticipation pour l'achat de votre résidence principale." },
              { icon: FiBriefcase, title: "Transmission", desc: "Protégez vos proches avec une fiscalité avantageuse en cas de décès, similaire à l'Assurance Vie." }
            ].map((item, index) => (
              <motion.div key={index} onClick={redirectToForm} className="bg-[#f0fcf9] p-8 rounded-[2rem] border border-[#00a48c]/10 hover:shadow-lg transition-all cursor-pointer group">
                <div className="w-14 h-14 rounded-2xl bg-white text-[#00a48c] flex items-center justify-center text-2xl mb-6 shadow-sm group-hover:scale-110 transition-transform"><SafeIcon icon={item.icon} /></div>
                <h3 className="text-xl font-black text-[#004d44] mb-3 uppercase tracking-tight">{item.title}</h3>
                <p className="text-sm text-[#006b5a]/80 font-medium leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center mb-8">
            <button onClick={redirectToForm} className="bg-[#004d44] text-white px-8 py-3 rounded-full font-black text-sm uppercase tracking-widest hover:bg-[#003832] transition-colors shadow-lg">
              Découvrir tous les avantages
            </button>
          </div>

          <div className="relative pt-8 md:pt-12 border-t border-gray-100">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-4xl font-black text-[#004d44] mb-4 uppercase tracking-tight">Les avantages de Simulation-rendement-PER©</h2>
              <div className="w-20 h-1.5 bg-[#00a48c] mx-auto rounded-full"></div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { title: "EPARGNEZ", desc: "Optez pour une retraite plus confortable demain et une baisse d’impôts aujourd’hui.", icon: FiTrendingUp },
                { title: "ECONOMISEZ", desc: "Réduisez vos impôts personnels ou professionnels à chaque versement sur votre PER.", icon: FiPercent },
                { title: "INVESTISSEZ", desc: "Investissez uniquement dans des projets durables labellisés Environnement et Solidaires.", icon: FiLeaf },
                { title: "SECURISEZ", desc: "Vous serez assuré par les meilleurs groupes de protection sociale en France.", icon: FiLock }
              ].map((adv, idx) => (
                <motion.div key={idx} whileHover={{ y: -10 }} className="relative p-8 bg-white border border-gray-100 rounded-[2.5rem] shadow-xl shadow-gray-200/50 flex flex-col items-center text-center" >
                  <div className="w-16 h-16 rounded-full bg-[#f0fcf9] text-[#00a48c] flex items-center justify-center text-2xl mb-6 border border-[#00a48c]/10">
                    <SafeIcon icon={adv.icon} />
                  </div>
                  <h3 className="text-xl font-black text-[#004d44] mb-4 tracking-tighter uppercase">{adv.title}</h3>
                  <p className="text-sm text-gray-500 font-bold leading-relaxed">{adv.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Profils & Risques */}
      <section id="etudes-de-cas" className="py-12 md:py-24 bg-[#f7f9fa]">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl md:text-4xl font-black text-center text-[#004d44] mb-10 md:mb-16 uppercase tracking-tight">3 profils de risque</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12">
            {/* Profil Prudent */}
            <motion.div whileHover={{ y: -5 }} className="bg-white rounded-[2rem] p-8 shadow-lg border-t-8 border-[#aaffd6] flex flex-col">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-xl font-black text-[#004d44] uppercase">Prudent</h3>
                  <span className="text-[#00a48c] font-black text-3xl">+ 4 %</span>
                </div>
                <div className="bg-[#aaffd6]/30 px-3 py-1 rounded-full text-[10px] font-black text-[#006b5a] uppercase tracking-widest">Serein</div>
              </div>
              <p className="text-xs text-gray-500 font-bold leading-relaxed mb-8">
                Il s’agit du profil le moins risqué. La partie la moins risquée du portefeuille représente 90 % à l’approche de la retraite, mais peut être aussi basse que 30 % lorsque la retraite est programmée dans plus de 15 ans.
              </p>
              <div className="mt-auto space-y-3 pt-6 border-t border-gray-100">
                <div className="flex justify-between text-sm font-bold"><span className="text-gray-400">150 € / mois</span> <span className="text-[#004d44]">99 709 €</span></div>
                <div className="flex justify-between text-sm font-bold"><span className="text-gray-400">250 € / mois</span> <span className="text-[#004d44]">166 181 €</span></div>
                <div className="flex justify-between text-sm font-bold"><span className="text-gray-400">500 € / mois</span> <span className="text-[#004d44]">332 363 €</span></div>
              </div>
            </motion.div>

            {/* Profil Équilibré */}
            <motion.div whileHover={{ y: -5 }} className="bg-[#00a48c] rounded-[2rem] p-8 shadow-2xl md:scale-105 z-10 flex flex-col text-white">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-xl font-black uppercase">Équilibré</h3>
                  <span className="text-[#aaffd6] font-black text-3xl">+ 7 %</span>
                </div>
                <div className="bg-white/20 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border border-white/30">Conseillé</div>
              </div>
              <p className="text-xs text-white/80 font-bold leading-relaxed mb-8">
                Il s’agit d’un profil modéré en termes de risque. La partie la moins risquée du portefeuille représente 70 % à l’approche de la retraite, mais peut être aussi basse que 0 % lorsque la retraite est programmée dans plus de 12 ans.
              </p>
              <div className="mt-auto space-y-3 pt-6 border-t border-white/20">
                <div className="flex justify-between text-sm font-bold"><span className="text-white/60">150 € / mois</span> <span>161 658 €</span></div>
                <div className="flex justify-between text-sm font-bold"><span className="text-white/60">250 € / mois</span> <span>269 430 €</span></div>
                <div className="flex justify-between text-sm font-bold"><span className="text-white/60">500 € / mois</span> <span>538 860 €</span></div>
              </div>
            </motion.div>

            {/* Profil Dynamique */}
            <motion.div whileHover={{ y: -5 }} className="bg-white rounded-[2rem] p-8 shadow-lg border-t-8 border-[#004d44] flex flex-col">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-xl font-black text-[#004d44] uppercase">Dynamique</h3>
                  <span className="text-[#006b5a] font-black text-3xl">+ 10 %</span>
                </div>
                <div className="bg-[#004d44]/10 px-3 py-1 rounded-full text-[10px] font-black text-[#004d44] uppercase tracking-widest">Performance</div>
              </div>
              <p className="text-xs text-gray-500 font-bold leading-relaxed mb-8">
                C’est le profil le plus risqué. La poche peu risquée représente 50 % du portefeuille à l’approche de la retraite et 0 % tant que l’horizon de retraite est supérieur à 10 ans.
              </p>
              <div className="mt-auto space-y-3 pt-6 border-t border-gray-100">
                <div className="flex justify-between text-sm font-bold"><span className="text-gray-400">150 € / mois</span> <span className="text-[#004d44]">272 883 €</span></div>
                <div className="flex justify-between text-sm font-bold"><span className="text-gray-400">250 € / mois</span> <span className="text-[#004d44]">454 805 €</span></div>
                <div className="flex justify-between text-sm font-bold"><span className="text-gray-400">500 € / mois</span> <span className="text-[#004d44]">909 611 €</span></div>
              </div>
            </motion.div>
          </div>
          
          <div className="text-center">
            <p className="text-[10px] md:text-xs font-bold text-gray-400 uppercase tracking-widest italic">
              * Montant de capital après 30 ans d’investissement
            </p>
          </div>
        </div>
      </section>

      {/* SECTION AVIS GOOGLE */}
      <section id="temoignages" className="py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-5xl font-black text-[#004d44] mb-6 uppercase tracking-tight">Ils nous font confiance</h2>
            <div className="flex flex-col items-center gap-2">
               <div className="flex items-center gap-2">
                 <span className="text-3xl font-black text-[#004d44]">5.0</span>
                 <div className="flex text-yellow-400 text-xl">
                   <SafeIcon icon={FiStar} className="fill-current" /><SafeIcon icon={FiStar} className="fill-current" /><SafeIcon icon={FiStar} className="fill-current" /><SafeIcon icon={FiStar} className="fill-current" /><SafeIcon icon={FiStar} className="fill-current" />
                 </div>
               </div>
               <p className="text-sm font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                 <span className="w-5 h-5 bg-[#4285F4] text-white rounded-full flex items-center justify-center text-[10px] font-black">G</span> Avis Google | 78 avis
               </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {[
              { 
                name: "Marc Lachaud", 
                date: "20 décembre 2022", 
                txt: "« Super satisfait de mon expérience pour mon PER. Les employés étaient sympas et ont été très clairs. Je suis rassuré pour mon investissement. »" 
              },
              { 
                name: "Mario Legrand", 
                date: "18 novembre 2022", 
                txt: "« Grâce à cette entreprise pour mon PER, j’ai pu bénéficier d’une réduction d’impôt conséquente. Leur assistance était de qualité supérieure, les conseillers clairs, compétents et disponibles pour répondre à mes questions. »" 
              },
              { 
                name: "Viviane S.", 
                date: "5 août 2022", 
                txt: "« Je suis très contente de l’expérience que j’ai eue en travaillant avec les experts. Les conseillers ont été très attentifs et ont pris le temps de m’expliquer les options disponibles. »" 
              }
            ].map((avis, idx) => (
              <motion.div key={idx} whileHover={{ y: -5 }} className="bg-[#f8fafb] p-8 rounded-[2rem] border border-gray-100 shadow-sm flex flex-col">
                <div className="flex text-yellow-400 mb-4 text-sm">
                  <SafeIcon icon={FiStar} className="fill-current" /><SafeIcon icon={FiStar} className="fill-current" /><SafeIcon icon={FiStar} className="fill-current" /><SafeIcon icon={FiStar} className="fill-current" /><SafeIcon icon={FiStar} className="fill-current" />
                </div>
                <p className="text-sm font-medium text-[#006b5a] leading-relaxed mb-8 flex-grow italic">
                  {avis.txt}
                </p>
                <div className="pt-6 border-t border-gray-200">
                  <div className="font-black text-[#004d44] text-sm uppercase">{avis.name}</div>
                  <div className="text-[10px] font-bold text-gray-400 mt-1 uppercase tracking-widest">{avis.date}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION PROFESSIONNELS EXIGEANTS */}
      <section className="py-16 md:py-32 bg-gradient-to-br from-[#00a48c] to-[#006b5a] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'radial-gradient(#fff 2px,transparent 2px)', backgroundSize: '30px 30px' }}></div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="max-w-4xl mb-16 md:mb-24 text-center lg:text-left">
            <h2 className="text-3xl md:text-6xl font-black mb-8 leading-tight uppercase tracking-tight">
              Le Plan Épargne Retraite pour les professionnels exigeants
            </h2>
            <div className="w-24 h-2 bg-white/30 mb-8 rounded-full mx-auto lg:mx-0"></div>
            <p className="text-lg md:text-2xl font-bold text-[#aaffd6] leading-relaxed">
              Un service sur-mesure, sélectionnant les meilleurs PER du marché pour optimiser votre fiscalité, votre patrimoine et votre transmission.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-12 mb-20 md:mb-32">
            {[
              { cat: "PROFESSIONNELS DE SANTÉ", desc: "Optimisation fiscale & transmission du patrimoine", icon: FiHeart },
              { cat: "AVOCATS", desc: "Une solution flexible adaptée à votre rythme professionnel", icon: FiAnchor },
              { cat: "DIRIGEANTS / INDÉPENDANTS", desc: "Des PER compatibles avec votre statut et vos revenus variables", icon: FiBriefcase }
            ].map((prof, idx) => (
              <motion.div key={idx} whileHover={{ scale: 1.02 }} className="bg-white/10 border border-white/20 p-8 md:p-10 rounded-[2.5rem] backdrop-blur-md group hover:bg-white/20 transition-all">
                <div className="w-14 h-14 rounded-2xl bg-white text-[#00a48c] flex items-center justify-center text-2xl mb-8 group-hover:scale-110 transition-transform shadow-lg">
                  <SafeIcon icon={prof.icon} />
                </div>
                <h3 className="text-xl font-black mb-4 tracking-tighter uppercase leading-tight">{prof.cat}</h3>
                <p className="text-sm font-bold text-white/70 uppercase tracking-widest leading-relaxed">{prof.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="bg-white/50 backdrop-blur-lg border border-white/20 shadow-2xl shadow-[#004d44]/20 rounded-[2.5rem] md:rounded-[4rem] p-8 md:p-12 relative overflow-hidden group">
            <div className="grid lg:grid-cols-5 gap-8 md:gap-12 items-center relative z-10">
              <div className="lg:col-span-3">
                <h3 className="text-3xl md:text-4xl font-black text-white mb-8 leading-tight">
                  Une sélection des meilleurs PER du marché, avec l'accompagnement d'un expert.
                </h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 mt-1 rounded-full bg-white/20 text-white flex items-center justify-center shrink-0 border-2 border-white/30">
                      <SafeIcon icon={FiShield} />
                    </div>
                    <div>
                      <h4 className="font-bold text-white uppercase tracking-wider">Sécurité des fonds</h4>
                      <p className="text-white/70 text-sm">Vos investissements sont protégés par les meilleurs mécanismes de garantie.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 mt-1 rounded-full bg-white/20 text-white flex items-center justify-center shrink-0 border-2 border-white/30">
                      <SafeIcon icon={FiTrendingUp} />
                    </div>
                    <div>
                      <h4 className="font-bold text-white uppercase tracking-wider">Performance optimisée</h4>
                      <p className="text-white/70 text-sm">Nous sélectionnons des fonds performants pour dynamiser votre épargne.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 mt-1 rounded-full bg-white/20 text-white flex items-center justify-center shrink-0 border-2 border-white/30">
                      <SafeIcon icon={FiClock} />
                    </div>
                    <div>
                      <h4 className="font-bold text-white uppercase tracking-wider">Gain de temps administratif</h4>
                      <p className="text-white/70 text-sm">Nous nous occupons de tout, de la souscription au suivi de votre dossier.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="lg:col-span-2 bg-white rounded-3xl p-8 text-center flex flex-col items-center shadow-xl mt-8 lg:mt-0">
                <div className="w-16 h-16 rounded-2xl bg-[#f0fcf9] text-[#00a48c] flex items-center justify-center mb-4 border-4 border-white shadow-inner">
                  <SafeIcon icon={FiTarget} size={32} />
                </div>
                <p className="text-xl font-black mb-6 uppercase tracking-tighter leading-tight text-[#004d44]">
                  Nos solutions : <br/> un PER adapté à votre statut.
                </p>
                <button onClick={redirectToForm} className="w-full bg-[#00a48c] text-white px-8 py-4 rounded-full font-black text-base uppercase tracking-widest hover:scale-105 transition-all shadow-lg shadow-[#00a48c]/30 flex items-center justify-center gap-3">
                  Simuler avec un expert <SafeIcon icon={FiArrowRight} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comment ça marche */}
      <section id="comment-ca-marche" className="py-12 md:py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <h2 className="text-2xl md:text-5xl font-black text-center text-[#004d44] mb-12 md:mb-20 uppercase tracking-tight">Comment ça marche ?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 relative">
            {[
              { step: 1, title: "Simulation", icon: FiMonitor, desc: "Découvrez votre solution en quelques minutes." },
              { step: 2, title: "Conseil", icon: FiPhoneCall, desc: "Un expert vous rappelle gratuitement." },
              { step: 3, title: "Lancement", icon: FiSend, desc: "Nous gérons tout votre administratif.", highlight: true }
            ].map((s, i) => (
              <motion.div key={i} whileHover={{ y: -5 }} onClick={redirectToForm} className="relative bg-white p-6 md:p-8 rounded-2xl md:rounded-[2.5rem] shadow-xl border border-[#f0fcf9] z-10 text-center cursor-pointer">
                <div className={`w-16 h-16 md:w-20 md:h-20 mx-auto rounded-full flex items-center justify-center mb-6 relative ${s.highlight ? 'bg-[#00a48c] text-white shadow-lg shadow-[#00a48c]/30' : 'bg-[#f0fcf9] text-[#00a48c]'}`}>
                  <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full flex items-center justify-center text-white font-black text-xs border-2 border-white bg-[#004d44] shadow-md">{s.step}</div>
                  <SafeIcon icon={s.icon} className="text-2xl md:text-3xl" />
                </div>
                <h3 className="text-xl font-black text-[#004d44] mb-2 uppercase tracking-tight">{s.title}</h3>
                <p className="text-sm text-[#006b5a]/60 font-medium">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-12 md:py-24 bg-[#00a48c]">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-[2rem] md:rounded-[4rem] p-8 md:p-16 shadow-2xl flex flex-col items-center text-center">
            <h2 className="text-2xl md:text-5xl font-black text-[#004d44] mb-4 md:mb-6 tracking-tight uppercase">
              Prêts à prendre le contrôle de votre PER ?
            </h2>
            <p className="text-lg md:text-xl font-bold text-[#00a48c] mb-8 md:mb-12 italic">
              Simulez vos gains en quelques clics !
            </p>
            <button onClick={redirectToForm} className="bg-[#00a48c] text-white w-full md:w-auto px-12 md:px-16 py-4 md:py-6 rounded-full font-black text-lg md:text-2xl shadow-xl hover:scale-105 transition-all flex items-center justify-center gap-4 uppercase tracking-tighter">
              Faire une simulation <SafeIcon icon={FiSend} />
            </button>
          </div>
        </div>
      </section>

      <Footer currentTheme="light" />
    </div>
  );
};

export default HomeLight;